import React from 'react';
import { usePage } from '@inertiajs/react';
import {
  LogIn, Link2, Share2, DollarSign, TrendingUp, Clock, BarChart3,
} from 'lucide-react';
import { PublicLayout } from '../../design-system/components/layouts';
import { Button } from '../../design-system/components';

const ICONS = { Link2, Share2, DollarSign, TrendingUp, Clock, BarChart3 };

function SignInForm({ children, className }) {
  return (
    <form action="/auth/speakflow" method="post" className={className}>
      <input
        type="hidden"
        name="authenticity_token"
        value={document.querySelector('meta[name="csrf-token"]')?.content ?? ''}
      />
      {children}
    </form>
  );
}

function BrandHeadline({ text }) {
  const parts = text.split(/<brand>(.*?)<\/brand>/);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <span key={i} className="text-fg-brand">{part}</span>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

export default function Home() {
  const { props } = usePage();
  const { default_campaign, content } = props;
  const appName = props.app_config?.company_name ?? 'Speakflow';

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-3xl text-center py-20 sm:py-28">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-fg tracking-tight leading-[1.1]">
            <BrandHeadline text={content.hero.headline} />
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-fg-secondary max-w-2xl mx-auto leading-relaxed">
            {content.hero.subtitle}
          </p>

          {/* CTA */}
          <div className="mt-10">
            <SignInForm>
              <Button type="submit" size="lg" className="text-base px-8 py-3">
                <LogIn size={20} />
                {content.hero.cta}
              </Button>
            </SignInForm>
            <div className="mt-4 text-sm text-fg-tertiary">
              Don't have a {appName} account?{' '}
              <a
                href={content.hero.signup_url}
                className="text-fg font-medium underline underline-offset-2 hover:text-fg-secondary transition-colors"
              >
                Sign up
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="border-t border-edge-strong">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl font-bold text-fg text-center">
            {content.why.headline}
          </h2>
          <p className="mt-4 text-base text-fg-secondary text-center max-w-2xl mx-auto">
            {content.why.subtitle}
          </p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
            {content.why.items.map(({ icon, title, description }) => {
              const Icon = ICONS[icon] || DollarSign;
              return (
                <div key={title}>
                  <div className="w-10 h-10 rounded-lg bg-surface-brand-light text-fg-brand flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-fg">{title}</h3>
                  <p className="mt-2 text-sm text-fg-secondary leading-relaxed">{description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-surface-secondary border-t border-edge-strong">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <h2 className="text-2xl sm:text-3xl font-bold text-fg text-center">
            {content.how.headline}
          </h2>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
            {content.how.items.map(({ icon, title, description }) => {
              const Icon = ICONS[icon] || DollarSign;
              return (
                <div key={title}>
                  <div className="w-10 h-10 rounded-lg bg-surface-inverse text-fg-inverse flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-fg">{title}</h3>
                  <p className="mt-2 text-sm text-fg-secondary leading-relaxed">{description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      {content.stats?.length > 0 && (
        <section className="border-t border-edge-strong">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12 text-center max-w-3xl mx-auto">
              {content.stats.map(({ label, value }) => (
                <div key={label}>
                  <p className="text-3xl sm:text-4xl font-bold text-fg">{value}</p>
                  <p className="mt-2 text-sm text-fg-secondary">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Custom partnerships */}
      {content.custom && (
        <section className="bg-surface-secondary border-t border-edge-strong">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-fg">
              {content.custom.headline}
            </h2>
            <p className="mt-4 text-base text-fg-secondary max-w-lg mx-auto">
              {content.custom.subtitle}
            </p>
            <div className="mt-8">
              <a href={`mailto:${content.custom.email}`}>
                <Button size="lg" variant="secondary" className="text-base px-8 py-3">
                  {content.custom.cta}
                </Button>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="border-t border-edge-strong">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-fg">
            {content.bottom_cta.headline}
          </h2>
          <p className="mt-4 text-base text-fg-secondary max-w-lg mx-auto">
            {content.bottom_cta.subtitle}
          </p>
          <div className="mt-8">
            <SignInForm>
              <Button type="submit" size="lg" className="text-base px-8 py-3">
                <LogIn size={20} />
                {content.bottom_cta.cta}
              </Button>
            </SignInForm>
            <div className="mt-4 text-sm text-fg-tertiary">
              Don't have a {appName} account?{' '}
              <a
                href={content.hero.signup_url}
                className="text-fg font-medium underline underline-offset-2 hover:text-fg-secondary transition-colors"
              >
                Sign up
              </a>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
